import { useRef, useState } from 'react'
import { useFeatureFlag } from '../lib/featureFlags'
import { spreadsheetsApi } from '../api/spreadsheets'
import { saveExportFile } from '../api/exports'
import { ExportMenu, type ExportFormat } from './ui'
import './shared.css'
import {
  combineTables,
  csvBlob,
  fileSafeName,
  tablesFromElement,
  type ReportTable,
} from '../lib/spreadsheet'

/**
 * F-140 (TASK-0394, REQ-026-004) — un solo pulsante "Esporta" (CSV, Excel, PDF) di un report, nella barra del titolo della pagina.
 *
 * - La tabella e' quella GIA' A VIDEO: di default il componente legge le tabelle visibili dentro il contenuto della
 *   pagina (`.md-content`), oppure `getTable()` la fornisce la pagina. Nessuna nuova richiesta di dati.
 * - CSV: costruito nel browser, `;` RFC 4180, BOM UTF-8, con la stessa protezione dalle formule dei file del server.
 * - Excel: `POST api/spreadsheets/xlsx` (anche li' ogni testo e' scritto come testo, mai come formula).
 * - PDF: `POST api/spreadsheets/pdf` (C-14), A4 orizzontale con intestazione Equal, utente e data.
 * - Le tabelle con dati personali per riga (lead, clienti, telefoni...) NON si esportano dal browser: servirebbe un
 *   export sul server, con approvazione e audit. Il componente le lascia fuori e lo dice.
 * - Visibile solo con il flag `export.excel` (spento finche' non lo accende il proprietario).
 */
export function ReportExportButtons({ title, getTable }: { title: string; getTable?: () => ReportTable | null }) {
  const enabled = useFeatureFlag('export.excel')
  const anchor = useRef<HTMLSpanElement>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')

  if (!enabled) return null

  function collect(): ReportTable | null {
    setMessage('')
    if (getTable) {
      const table = getTable()
      if (!table || table.rows.length === 0) setMessage('Nessun dato da esportare.')
      return table && table.rows.length > 0 ? table : null
    }
    const root = anchor.current?.closest('.md-content') ?? document.body
    const { tables, skippedPersonal } = tablesFromElement(root)
    const combined = combineTables(tables)
    if (!combined) {
      setMessage(skippedPersonal > 0
        ? 'Questa pagina mostra dati personali per riga: non si esportano dal browser.'
        : 'Nessuna tabella da esportare in questa pagina.')
      return null
    }
    if (skippedPersonal > 0) {
      setMessage(`${skippedPersonal} ${skippedPersonal === 1 ? 'elenco con dati personali è escluso' : 'elenchi con dati personali sono esclusi'} dal file.`)
    }
    return combined
  }

  function toCsv() {
    const table = collect()
    if (!table) return
    saveExportFile({ blob: csvBlob(table), filename: `${fileSafeName(title)}.csv` })
  }

  async function toServer(format: 'xlsx' | 'pdf') {
    const table = collect()
    if (!table) return
    setBusy(true)
    try {
      const request = {
        title,
        columns: table.columns,
        rows: table.rows,
        ...(table.autoFilter === false ? { autoFilter: false } : {}),
      }
      if (format === 'pdf') await spreadsheetsApi.downloadPdf(request)
      else await spreadsheetsApi.downloadXlsx(request)
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Errore durante la creazione del file.')
    } finally {
      setBusy(false)
    }
  }

  function onExport(format: ExportFormat) {
    if (format === 'csv') toCsv()
    else void toServer(format)
  }

  return (
    <span ref={anchor} className="ecrm-inline-actions d-print-none report-export-buttons">
      <style>{PRINT_CSS}</style>
      <ExportMenu size="sm" busy={busy} onExport={onExport} title="Esporta la tabella a video in CSV, Excel o PDF" />
      {message && <span className="ecrm-inline-message" role="status">{message}</span>}
    </span>
  )
}

/** Foglio di stile di stampa: via menu, barra in alto e pulsanti, la pagina occupa tutto il foglio. */
const PRINT_CSS = `@media print {
  .md-sidebar, .md-sidebar-overlay, .md-topbar { display: none !important; }
  .md-main { margin-left: 0 !important; }
  .md-content { padding: 0 !important; }
  table { page-break-inside: auto; }
  tr { page-break-inside: avoid; page-break-after: auto; }
  thead { display: table-header-group; }
}`
